import "./HeaderItem.scss";
import { useState, useEffect } from "react";
import cn from "classnames";
import {
  GridRow,
  GridCell,
  Link,
  CaretCircleDownIcon,
  Button,
  IconLookup,
  ArrowRightIcon,
  ArrowLineUpRightIcon,
} from "/App.Components";
import { ExpandedMenuProps } from "/Models/Generated/ExpandedMenuProps";
import { Collapse } from "react-collapse";
import { ColorTheme } from "/Models/Models/ColorTheme";

export interface HeaderItemExtendedProps extends ExpandedMenuProps {
  mobileMenuIsOpen: boolean;
}

const HeaderItemMobile = ({
  menuName,
  introHeading,
  introText,
  overviewLink,
  children,
  mobileMenuIsOpen,
  ctaLink,
  ctaIcon,
}: HeaderItemExtendedProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleExpandedMenuItem = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!mobileMenuIsOpen) setIsOpen(false);
  }, [mobileMenuIsOpen]);

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.code === "Escape") {
        isOpen && setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen]);

  return (
    <li className="header-item-norad">
      <button
        onClick={toggleExpandedMenuItem}
        tabIndex={mobileMenuIsOpen ? 0 : -1}
        className={cn("header-item-norad__button-mobile", {
          "header-item-norad__button-mobile--is-open": isOpen,
        })}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>{menuName}</span>
        <CaretCircleDownIcon size="32" thickness="Light" />
      </button>
      <Collapse
        isOpened={isOpen}
        theme={{ collapse: "header-item-norad__expanded-menu-item" }}
      >
        <div className="header-item-norad__expanded-menu-item-content">
          <GridRow>
            <GridCell
              startColsDesktop={1}
              spanColsDesktop={4}
              startColsTablet={1}
              spanColsTablet={6}
              startColsMobile={1}
              spanColsMobile={6}
            >
              <div className="header-item-norad__expanded-menu-item-intro">
                {introHeading && (
                  <h2 className="header-item-norad__expanded-menu-item-heading">
                    {introHeading}
                  </h2>
                )}
                {overviewLink && (
                  <Link
                    size="small"
                    icon={
                      overviewLink.isExternal
                        ? ArrowLineUpRightIcon
                        : ArrowRightIcon
                    }
                    iconPlacement="after"
                    iconSize="20"
                    tabIndex={isOpen ? 0 : -1}
                    {...overviewLink}
                  />
                )}
                {introText && (
                  <p className="header-item-norad__expanded-menu-item-text">
                    {introText}
                  </p>
                )}
                {ctaLink?.text && (
                  <div className="header-item-norad__expanded-menu-item-intro__cta-wrapper">
                    <Button
                      {...(ctaIcon && {
                        iconBefore: (
                          <IconLookup
                            iconType={ctaIcon}
                            iconProps={{
                              thickness: "Reg",
                              size: "24",
                              color: ColorTheme.black,
                            }}
                          />
                        ),
                      })}
                      size="m"
                      style={"primary"}
                      label={ctaLink.text}
                      href={ctaLink.href}
                      tabIndex={isOpen ? 0 : -1}
                    />
                  </div>
                )}
              </div>
            </GridCell>

            {children &&
              children.length !== 0 &&
              children.map((item, index) => (
                <GridCell
                  key={item.category}
                  startColsDesktop={index % 2 === 0 ? 6 : 10}
                  spanColsDesktop={3}
                  startColsTablet={index % 2 === 0 ? 1 : 4}
                  spanColsTablet={3}
                  startColsMobile={1}
                  spanColsMobile={6}
                >
                  <div className="header-item-norad__expanded-menu-item-category">
                    {item?.category && (
                      <h3 className="header-item-norad__expanded-menu-item-category-title">
                        {item.category}
                      </h3>
                    )}
                    <ul
                      aria-hidden={!isOpen}
                      className="header-item-norad__expanded-menu-item-category-list"
                    >
                      {item.links &&
                        item.links.length !== 0 &&
                        item.links.map((link, index) => (
                          <li
                            key={link.text && link.text + index}
                            className="header-item-norad__expanded-menu-item-category-link"
                          >
                            <Link
                              size="small"
                              {...link}
                              tabIndex={isOpen ? 0 : -1}
                            />
                          </li>
                        ))}
                    </ul>
                  </div>
                </GridCell>
              ))}
          </GridRow>
        </div>
      </Collapse>
    </li>
  );
};

export default HeaderItemMobile;
